import React, { useState } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { HandCoins, CheckCircle2, Phone } from "lucide-react";
import { useToast } from "../hooks/use-toast";
import { leadsApi, track } from "../api";
import useSeo from "../hooks/useSeo";

const SERVICES = ["Personal Care", "Homemaker Support", "Companionship", "Respite", "Transportation (where available)", "Community Support", "Other"];
const CONTACT_METHODS = ["Phone call", "Text message", "Email"];

const EMPTY = { name: "", phone: "", email: "", city: "", county: "", service: "", requestedStart: "", days: "", times: "", weeklyHours: "", transportation: "", preferredContact: "" };

const PrivatePay = () => {
  useSeo({
    title: "Private-Pay Home Care in Ohio | Caregivers Alliance Homecare",
    description: "Private-pay home care in Ohio: personal care, homemaker support, companionship, respite and transportation where available. Flexible scheduling, no waiver required. Request a consultation: (614) 526-1548.",
    keywords: "private pay home care Columbus Ohio, private pay caregiver Ohio, home care agency near me, private duty home care Ohio",
    path: "/private-pay",
  });
  const { toast } = useToast();
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const details = [
      form.city && `City: ${form.city}`, form.days && `Days: ${form.days}`,
      form.times && `Times: ${form.times}`, form.transportation && `Transportation needed: ${form.transportation}`,
      form.preferredContact && `Preferred contact: ${form.preferredContact}`,
    ].filter(Boolean).join(" • ");
    try {
      await leadsApi.create({
        name: form.name, contact: form.phone || form.email, email: form.email, phone: form.phone,
        source: "Website", org: "\u2014", referralType: "Private Pay",
        county: form.county, service: form.service, requestedStart: form.requestedStart, weeklyHours: form.weeklyHours,
        note: `Private-pay consultation request — ${details || "No additional details"}`,
      });
      toast({ title: "Consultation requested!", description: "We'll contact you about current rates and service availability." });
      track("private_pay_inquiry", form.county);
      setForm(EMPTY);
    } catch {
      toast({ title: "Something went wrong", description: "Please call us at (614) 526-1548.", variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <section className="brand-gradient text-white">
        <div className="mx-auto max-w-7xl px-4 py-14">
          <div className="inline-flex items-center gap-2 bg-white/15 rounded-full px-4 py-1.5 text-sm font-semibold mb-4">
            <HandCoins className="h-4 w-4" /> No waiver required
          </div>
          <h1 className="font-display font-extrabold text-4xl md:text-5xl">Private-Pay Home Care in Ohio</h1>
          <p className="mt-4 text-white/90 max-w-2xl">
            Flexible, person-centered support on your schedule. Contact us for current rates and service availability.
          </p>
        </div>
      </section>

      <section className="section bg-white">
        <div className="mx-auto max-w-7xl px-4 grid lg:grid-cols-2 gap-10 items-start">
          <div>
            <h2 className="font-display font-bold text-3xl text-[#0E2A33]">Services may include:</h2>
            <div className="mt-6 space-y-3">
              {SERVICES.filter((s) => s !== "Other").map((s) => (
                <div key={s} className="flex items-center gap-3 bg-[#F7FBFC] rounded-xl border border-slate-100 p-4">
                  <CheckCircle2 className="h-5 w-5 text-[#2E9E6A] shrink-0" />
                  <span className="text-slate-700 font-medium">{s}</span>
                </div>
              ))}
            </div>
            <p className="text-sm text-slate-500 mt-5">
              Actual service availability depends on staffing, location, schedule, and individual support requirements.
            </p>
            <a href="tel:6145261548" onClick={() => track("call_click", form.county)} className="inline-flex items-center gap-2 mt-4 text-[#0E5A72] font-semibold hover:underline">
              <Phone className="h-4 w-4" /> (614) 526-1548
            </a>
          </div>

          <form onSubmit={submit} data-testid="private-pay-form" className="bg-white rounded-2xl border border-slate-100 shadow-lg p-7 space-y-4">
            <h3 className="font-display font-bold text-xl text-[#0E2A33]">Request a Consultation</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label>Name</Label><Input data-testid="pp-name-input" required value={form.name} onChange={(e) => set("name")(e.target.value)} className="mt-1" /></div>
              <div><Label>Phone</Label><Input data-testid="pp-phone-input" required value={form.phone} onChange={(e) => set("phone")(e.target.value)} className="mt-1" /></div>
            </div>
            <div className="grid sm:grid-cols-3 gap-4">
              <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => set("email")(e.target.value)} className="mt-1" /></div>
              <div><Label>City</Label><Input value={form.city} onChange={(e) => set("city")(e.target.value)} className="mt-1" /></div>
              <div><Label>County</Label><Input data-testid="pp-county-input" value={form.county} onChange={(e) => set("county")(e.target.value)} className="mt-1" placeholder="Franklin" /></div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Requested Service</Label>
                <Select value={form.service} onValueChange={set("service")}>
                  <SelectTrigger data-testid="pp-service-select" className="mt-1"><SelectValue placeholder="Select service" /></SelectTrigger>
                  <SelectContent>{SERVICES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Desired Start Date</Label><Input type="date" value={form.requestedStart} onChange={(e) => set("requestedStart")(e.target.value)} className="mt-1" /></div>
            </div>
            <div className="grid sm:grid-cols-3 gap-4">
              <div><Label>Days</Label><Input value={form.days} onChange={(e) => set("days")(e.target.value)} className="mt-1" placeholder="Mon–Fri" /></div>
              <div><Label>Times</Label><Input value={form.times} onChange={(e) => set("times")(e.target.value)} className="mt-1" placeholder="Mornings" /></div>
              <div><Label>Approx. Weekly Hours</Label><Input value={form.weeklyHours} onChange={(e) => set("weeklyHours")(e.target.value)} className="mt-1" placeholder="12" /></div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Transportation Needed?</Label>
                <Select value={form.transportation} onValueChange={set("transportation")}>
                  <SelectTrigger className="mt-1"><SelectValue placeholder="Yes / No" /></SelectTrigger>
                  <SelectContent>{["Yes", "No", "Unsure"].map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Preferred Contact Method</Label>
                <Select value={form.preferredContact} onValueChange={set("preferredContact")}>
                  <SelectTrigger className="mt-1"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{CONTACT_METHODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <Button data-testid="pp-submit-btn" type="submit" disabled={busy} className="w-full bg-[#2E9E6A] hover:bg-[#23875A] text-white rounded-full h-12 font-semibold">
              {busy ? "Sending…" : "REQUEST A CONSULTATION"}
            </Button>
            <p className="text-xs text-slate-400 text-center">Contact us for current rates and service availability.</p>
          </form>
        </div>
      </section>
    </>
  );
};

export default PrivatePay;
